/**
 * Who a routed finding goes to at each site — five roles × six mines.
 *
 * The routing engine needs a named person for every role a finding can be
 * pushed to. The directory (`people.json` `people`, joined by name) covers
 * only part of that: only Gawler Deep has a Mine Control Dispatcher, only
 * Broken Ridge and Gawler Deep have a site Reliability Engineer, and no site
 * has an Operating Supervisor or Superintendent by that title.
 *
 * So each row here is one of three kinds, and says which:
 *
 * - **Directory person, own title** — `synthesised: false`, no `note`.
 * - **Directory person covering the role** — `synthesised: false` with a
 *   `note` saying why (Kalbarri's night supervisor, the OEM reliability lead
 *   Mount Isolde's site record names, the remote reliability centre covering
 *   a site that has no engineer). Every such cover is grounded in the data
 *   pack: `core.json` `mines[].reliability` or the person's title.
 * - **Synthesised** — `synthesised: true`, no `personId`. A proof-of-concept
 *   stand-in for a role the data pack does not name.
 *
 * `personId` is the directory email, verbatim — the one unique field that
 * table has besides the name. `scripts/oem/check-reliability.mjs` asserts
 * every non-synthesised row against `people.json`.
 *
 * Data Schema layer — seed data.
 *
 * @see src/versions/v1/schema/operations.ts — `SiteRoleAssignment`
 */

import type { MineCode } from '../../schema/machine.ts';
import type { SiteRole, SiteRoleAssignment } from '../../schema/operations.ts';

/** The five routable roles, operations side first. */
export const SITE_ROLES: readonly SiteRole[] = [
  'Operating Supervisor',
  'Superintendent',
  'Mine Control',
  'Reliability Engineer',
  'Maintenance Planner',
];

/** Which side of the house a role sits on. */
export const ROLE_SIDE: Readonly<Record<SiteRole, 'operations' | 'maintenance'>> = {
  'Operating Supervisor': 'operations',
  Superintendent: 'operations',
  'Mine Control': 'operations',
  'Reliability Engineer': 'maintenance',
  'Maintenance Planner': 'maintenance',
};

/** Directory person — in their own title, or covering the role (`note`). */
const dir = (
  mine: MineCode,
  role: SiteRole,
  personName: string,
  title: string,
  org: string,
  personId: string,
  note?: string,
): SiteRoleAssignment => ({
  role,
  mine,
  personName,
  personId,
  title,
  org,
  synthesised: false,
  ...(note ? { note } : {}),
});

/** Proof-of-concept stand-in. */
const syn = (mine: MineCode, role: SiteRole, personName: string, title: string, org: string): SiteRoleAssignment => ({
  role,
  mine,
  personName,
  title,
  org,
  synthesised: true,
});

/** Every site's five role holders. 30 rows. */
export const SITE_ROLE_ASSIGNMENTS: readonly SiteRoleAssignment[] = [
  // ── Kalbarri Gold ────────────────────────────────────────────────────────
  dir(
    'KG',
    'Operating Supervisor',
    'Shane Kerr',
    'Night Crew B Supervisor',
    'Kalbarri Gold',
    'shane.kerr@kalbarrigold.example',
    'Kalbarri\'s directory names one production supervisor — the Night Crew B supervisor — so he takes operator items for the site.',
  ),
  syn('KG', 'Superintendent', 'Rhys Tolhurst', 'Underground Superintendent', 'Kalbarri Gold'),
  syn('KG', 'Mine Control', 'Kim Arnott', 'Mine Controller', 'Kalbarri Gold'),
  dir(
    'KG',
    'Reliability Engineer',
    'Priya Raman',
    'Remote Monitoring Specialist (Reliability Centre)',
    'Meridian Equipment',
    'rms@meridian-eq.example',
    'Kalbarri has no site reliability engineer (core.json mines.KG.reliability is "—"); the Meridian Reliability Centre covers it remotely.',
  ),
  dir('KG', 'Maintenance Planner', 'Alicia Fenton', 'Maintenance Planner', 'Kalbarri Gold', 'alicia.fenton@kalbarrigold.example'),

  // ── Broken Ridge ─────────────────────────────────────────────────────────
  syn('BR', 'Operating Supervisor', 'Nick Pavlou', 'Shift Supervisor — Underground', 'Broken Ridge'),
  syn('BR', 'Superintendent', 'Joanne Whitby', 'Mining Superintendent', 'Broken Ridge'),
  syn('BR', 'Mine Control', 'Tyson Reid', 'Mine Controller', 'Broken Ridge'),
  dir('BR', 'Reliability Engineer', 'Priya Nair', 'Reliability Engineer', 'Broken Ridge', 'priya.nair@brokenridge.example'),
  dir('BR', 'Maintenance Planner', 'Marco Bellini', 'Maintenance Planner', 'Broken Ridge', 'marco.bellini@brokenridge.example'),

  // ── Mount Isolde (Redgum Mining Services) ────────────────────────────────
  syn('MI', 'Operating Supervisor', 'Cheryl Mahoney', 'Shift Supervisor — Load & Haul', 'Redgum Mining Services'),
  syn('MI', 'Superintendent', 'Grant Ellery', 'Production Superintendent', 'Redgum Mining Services'),
  syn('MI', 'Mine Control', 'Ana Folau', 'Mine Control Dispatcher', 'Mount Isolde Copper'),
  dir(
    'MI',
    'Reliability Engineer',
    'Hanna Virtanen',
    'Fleet Reliability Lead',
    'Meridian Equipment',
    'hanna.virtanen@meridian-eq.example',
    'Mount Isolde\'s site record names the OEM reliability lead as its reliability engineer (core.json mines.MI.reliability).',
  ),
  dir('MI', 'Maintenance Planner', 'Jorge Castillo', 'Maintenance Planner', 'Redgum Mining Services', 'jorge.castillo@redgum.example'),

  // ── Ochre Hill Gold ──────────────────────────────────────────────────────
  dir(
    'OH',
    'Operating Supervisor',
    'Jack Morrow',
    'Drill & Blast Supervisor',
    'Ochre Hill Gold',
    'jack.morrow@ochrehill.example',
    'Ochre Hill runs drills only, so the drill & blast supervisor is the operating supervisor.',
  ),
  syn('OH', 'Superintendent', 'Bernadette Quill', 'Mining Superintendent', 'Ochre Hill Gold'),
  syn('OH', 'Mine Control', 'Dev Raghavan', 'Pit Controller', 'Ochre Hill Gold'),
  dir(
    'OH',
    'Reliability Engineer',
    'Amelia Hart',
    'Surface Drilling Product Support Engineer',
    'Meridian Equipment',
    'amelia.hart@meridian-eq.example',
    'Ochre Hill has no site reliability engineer (core.json mines.OH.reliability is "—"); the OEM surface-drilling engineer covers the drill fleet.',
  ),
  dir('OH', 'Maintenance Planner', 'Mei Lin Tan', 'Maintenance Planner', 'Ochre Hill Gold', 'meilin.tan@ochrehill.example'),

  // ── Gawler Deep (+ Spinifex Underground) ─────────────────────────────────
  syn('GD', 'Operating Supervisor', 'Hollie Brandt', 'Shift Supervisor — Underground', 'Gawler Deep'),
  dir(
    'GD',
    'Superintendent',
    'Deng Achol',
    'Drill & Blast Superintendent (pit)',
    'Gawler Deep',
    'deng.achol@gawlerdeep.example',
    'The only superintendent in Gawler Deep\'s directory entry; takes escalations for the whole site in the proof of concept.',
  ),
  dir('GD', 'Mine Control', 'Harper Quinn', 'Mine Control Dispatcher', 'Gawler Deep', 'mine.control@gawlerdeep.example'),
  dir('GD', 'Reliability Engineer', 'Sofia Marchetti', 'Reliability Engineer', 'Gawler Deep', 'sofia.marchetti@gawlerdeep.example'),
  dir(
    'GD',
    'Maintenance Planner',
    'Callum Byrne',
    'Maintenance Planner (underground)',
    'Gawler Deep',
    'callum.byrne@gawlerdeep.example',
  ),

  // ── Mount Read Metals (Western Tiers Mining) ─────────────────────────────
  syn('MR', 'Operating Supervisor', 'Luke Brammall', 'Shift Supervisor', 'Western Tiers Mining'),
  syn('MR', 'Superintendent', 'Fern Oakley', 'Underground Superintendent', 'Western Tiers Mining'),
  syn('MR', 'Mine Control', 'Sione Havili', 'Mine Controller', 'Mount Read Metals'),
  dir(
    'MR',
    'Reliability Engineer',
    'Gareth Llewellyn',
    'Mining Manager',
    'Mount Read Metals',
    'gareth.llewellyn@mountreadmetals.example',
    'Mount Read\'s site record names the owner\'s mining manager as its reliability lead (core.json mines.MR.reliability).',
  ),
  dir(
    'MR',
    'Maintenance Planner',
    'Isla Mackenzie',
    'Maintenance Planner',
    'Western Tiers Mining',
    'isla.mackenzie@westerntiers.example',
  ),
];

/** The holder of `role` at `mine`. Every mine × role has exactly one. */
export function siteRole(mine: MineCode, role: SiteRole): SiteRoleAssignment {
  const hit = SITE_ROLE_ASSIGNMENTS.find((r) => r.mine === mine && r.role === role);
  if (!hit) throw new Error(`site-roles seed: no ${role} at ${mine}`);
  return hit;
}
